import { createRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { Route as AdminLayoutRoute } from "./_layout";
import { Badge, BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Card, CardBody, CardHeader } from "@/components/application/card/card";
import {
  DataTable,
  type Column,
} from "@/components/application/data-table/data-table";
import { ApiError } from "../../lib/apiClient";
import { useBudgetList } from "../../hooks/useBudgetQueries";
import type { BudgetListRow } from "../../schemas/budget";
import {
  useDistributionEvents,
  useDistributionTrigger,
  type AuditLogEntry,
  type TriggerDistributionResponse,
} from "../../hooks/useDistributionQueries";

const POLL_WINDOW_MS = 60_000;

export function DistributionPage() {
  const budgetsQuery = useBudgetList();
  const [selectedBudgetId, setSelectedBudgetId] = useState<string>("");
  const [iban, setIban] = useState<string>("");
  const [fromName, setFromName] = useState<string>("");
  const [polling, setPolling] = useState<boolean>(false);
  const [lastResult, setLastResult] = useState<TriggerDistributionResponse | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const trigger = useDistributionTrigger();
  const events = useDistributionEvents({ polling });

  const budgetSelectId = useId();
  const budgetHintId = useId();

  const approvedBudgets = useMemo(
    () => (budgetsQuery.data?.rows ?? []).filter((b) => b.status === "approved"),
    [budgetsQuery.data],
  );

  const selectedBudget = approvedBudgets.find((b) => b.budget_id === selectedBudgetId) ?? null;

  useEffect(() => {
    if (!polling) return;
    const t = setTimeout(() => setPolling(false), POLL_WINDOW_MS);
    return () => clearTimeout(t);
  }, [polling, lastResult]);

  const submit = useCallback(async () => {
    if (!selectedBudgetId) {
      setSubmitError("Vyber rozpočet, který chceš distribuovat.");
      return;
    }
    if (!iban.trim()) {
      setSubmitError("IBAN je povinný.");
      return;
    }
    setSubmitError(null);
    try {
      const result = await trigger.mutateAsync({
        budgetId: selectedBudgetId,
        iban_cz: iban.trim(),
        from_name_override: fromName.trim() || undefined,
      });
      setLastResult(result);
      setPolling(true);
      void events.refetch();
    } catch (e) {
      if (e instanceof ApiError) {
        setSubmitError(mapTriggerError(e));
      } else {
        setSubmitError(e instanceof Error ? e.message : "Spuštění distribuce selhalo.");
      }
    }
  }, [selectedBudgetId, iban, fromName, trigger, events]);

  return (
    <section aria-labelledby="distribution-title" className="max-w-7xl mx-auto space-y-6">
      <header className="flex flex-col gap-1">
        <h2 id="distribution-title" className="text-2xl font-bold text-primary m-0">
          Distribuce
        </h2>
        <p className="text-base text-tertiary m-0">
          Rozeslání platebních QR kódů a odkazů příjemcům vybraného schváleného rozpočtu.
        </p>
      </header>

      <Card>
        <CardHeader title="Spuštění distribuce" />
        <CardBody>
          {budgetsQuery.isLoading && <p className="text-tertiary m-0">Načítám rozpočty…</p>}
          {budgetsQuery.isError && (
            <InlineAlert variant="error">Načtení rozpočtů selhalo. Zkus obnovit stránku.</InlineAlert>
          )}
          {!budgetsQuery.isLoading && !budgetsQuery.isError && approvedBudgets.length === 0 && (
            <InlineAlert variant="warning">
              Žádný schválený rozpočet není k dispozici. Schval rozpočet v sekci „Rozpočty" a vrať se sem.
            </InlineAlert>
          )}

          {approvedBudgets.length > 0 && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
              className="flex flex-col gap-4"
            >
              <div className="flex flex-col gap-1.5">
                <label htmlFor={budgetSelectId} className="text-sm font-medium text-secondary">
                  Rozpočet k distribuci{" "}
                  <span aria-hidden="true" className="text-error-primary">
                    *
                  </span>
                </label>
                <select
                  id={budgetSelectId}
                  aria-describedby={budgetHintId}
                  aria-required="true"
                  className="block w-full min-h-touch rounded-lg bg-primary px-3 py-2 text-md text-primary ring-1 ring-inset ring-primary outline-focus-ring focus:ring-2 focus:ring-brand disabled:opacity-50"
                  value={selectedBudgetId}
                  onChange={(e) => setSelectedBudgetId(e.target.value)}
                  disabled={trigger.isPending}
                >
                  <option value="">— Vyber —</option>
                  {approvedBudgets.map((b) => (
                    <option key={b.budget_id} value={b.budget_id}>
                      {budgetLabel(b)}
                    </option>
                  ))}
                </select>
                <p id={budgetHintId} className="text-sm text-tertiary">
                  Vidíš pouze schválené rozpočty.
                </p>
              </div>

              <Input
                label="IBAN platebního účtu"
                isRequired
                hint="Český IBAN — CZ + 22 číslic."
                value={iban}
                onChange={setIban}
                placeholder="CZ6508000000192000145399"
                isDisabled={trigger.isPending}
                autoComplete="off"
              />

              <Input
                label="Jméno odesílatele (volitelné)"
                hint="Pokud necháš prázdné, použije se výchozí název Střediska."
                value={fromName}
                onChange={setFromName}
                placeholder="Středisko Praha"
                isDisabled={trigger.isPending}
              />

              {submitError && <InlineAlert variant="error">{submitError}</InlineAlert>}

              <div className="flex gap-2 pt-2">
                <Button
                  type="submit"
                  color="primary"
                  isDisabled={trigger.isPending || !selectedBudgetId || !iban.trim()}
                  isLoading={trigger.isPending}
                >
                  {trigger.isPending ? "Spouštím distribuci…" : "Spustit distribuci"}
                </Button>
              </div>

              {selectedBudget && (
                <p className="text-xs text-tertiary m-0">
                  Vybráno: <strong className="text-primary">{selectedBudget.billing_group_code}</strong>,
                  měsíc <strong className="text-primary">{selectedBudget.month}</strong>, částka{" "}
                  <strong className="text-primary">{formatCzk(selectedBudget.total_amount_kc)}</strong>.
                </p>
              )}
            </form>
          )}
        </CardBody>
      </Card>

      {lastResult && (
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-primary m-0">Výsledek poslední distribuce</h3>
            {polling ? (
              <BadgeWithDot color="blue" type="pill-color">
                Aktualizuje se každých 5 s
              </BadgeWithDot>
            ) : (
              <Badge color="gray">Sledování ukončeno</Badge>
            )}
          </CardHeader>
          <CardBody>
            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4 m-0">
              <div className="flex flex-col gap-1">
                <dt className="text-xs font-medium text-tertiary m-0">Vytvořeno úhrad</dt>
                <dd className="text-2xl font-bold text-primary m-0">{lastResult.intents_created}</dd>
              </div>
              <div className="flex flex-col gap-1">
                <dt className="text-xs font-medium text-tertiary m-0">E-maily v frontě</dt>
                <dd className="text-2xl font-bold text-primary m-0">{lastResult.emails_enqueued}</dd>
              </div>
              <div className="flex flex-col gap-1">
                <dt className="text-xs font-medium text-tertiary m-0">QR kódy</dt>
                <dd className="text-2xl font-bold text-primary m-0">{lastResult.qr_generated}</dd>
              </div>
              <div className="flex flex-col gap-1">
                <dt className="text-xs font-medium text-tertiary m-0">Přeskočeno</dt>
                <dd className="text-2xl font-bold text-primary m-0">{lastResult.skipped_count}</dd>
              </div>
            </dl>
            {lastResult.skipped_count > 0 && (
              <p className="text-xs text-tertiary m-0 mt-4">
                Přeskočení typicky znamená uživatele bez platného souhlasu.
              </p>
            )}
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title="Nedávná distribuční aktivita" />
        <CardBody>
          {events.isLoading && <p className="text-tertiary m-0">Načítám aktivitu…</p>}
          {events.isError && <InlineAlert variant="error">Načtení aktivity selhalo.</InlineAlert>}
          {!events.isLoading && !events.isError && (
            <DataTable
              caption="Posledních 20 distribučních záznamů (e-maily, magic odkazy, upomínky)"
              columns={eventColumns}
              rows={events.data ?? []}
              rowKey={(e) => e.id}
              emptyMessage="Zatím žádná distribuční aktivita."
            />
          )}
        </CardBody>
      </Card>
    </section>
  );
}

const eventColumns: Column<AuditLogEntry>[] = [
  {
    key: "action",
    header: "Akce",
    cell: (e) => actionLabel(e.action),
  },
  {
    key: "tenant",
    header: "Středisko",
    cell: (e) =>
      e.tenantId ? (
        <span className="text-xs">{e.tenantId.slice(0, 8)}…</span>
      ) : (
        <span className="text-tertiary">—</span>
      ),
  },
  {
    key: "time",
    header: "Čas",
    cell: (e) => new Date(e.createdAt).toLocaleString("cs-CZ"),
  },
];

function budgetLabel(b: BudgetListRow): string {
  const eur = b.eur_advisory_flag ? " (obsahuje EUR)" : "";
  return `${b.billing_group_code} • ${b.month} • ${formatCzk(b.total_amount_kc)}${eur}`;
}

function formatCzk(amount: string | number): string {
  const n = typeof amount === "string" ? Number(amount) : amount;
  if (!Number.isFinite(n)) return String(amount);
  return n.toLocaleString("cs-CZ", { maximumFractionDigits: 2 }) + " Kč";
}

function actionLabel(action: string): string {
  switch (action) {
    case "distribution_triggered":
      return "Spuštěna distribuce";
    case "distribution_completed":
      return "Distribuce dokončena";
    case "distribution_skipped_no_consent":
      return "Přeskočen příjemce — chybí souhlas";
    case "magic_link_consumed":
      return "Magic odkaz použit";
    case "payment_reminder_sent":
      return "Odeslána upomínka";
    default:
      return action;
  }
}

function mapTriggerError(e: ApiError): string {
  switch (e.errorCode) {
    case "DISTRIBUTION_BUDGET_NOT_FOUND":
      return "Vybraný rozpočet neexistuje.";
    case "DISTRIBUTION_BUDGET_NOT_APPROVED":
      return "Rozpočet musí být schválen, než ho lze distribuovat.";
    case "DISTRIBUTION_IBAN_INVALID":
      return "IBAN má neplatný formát. Použij CZ + 22 číslic.";
    case "DESKTOP_REQUIRED":
      return "Distribuci lze spustit jen z desktop prohlížeče (anti-omyl pro reálné odesílání e-mailů).";
    case "VALIDATION_ERROR":
      return `Validační chyba: ${e.message}`;
    default:
      return e.message || `HTTP ${e.status}`;
  }
}

export const Route = createRoute({
  getParentRoute: () => AdminLayoutRoute,
  path: "/admin/distribution",
  component: DistributionPage,
});
